import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO component — manages all meta tags + structured data per page.
 *
 * Supports multi-language pages via `htmlLang`, `ogLocale`, and `hreflang` array
 * so Google can serve the right version per user locale.
 */
const SEO = ({
  title,
  description,
  canonical,
  keywords,
  ogType = 'website',
  ogImage,
  ogImageWidth = '1200',
  ogImageHeight = '630',
  structuredData,
  noindex = false,
  htmlLang = 'fr-BE',
  ogLocale = 'fr_BE',
  hreflang, // array of { lang, url } pairs — e.g. [{ lang: 'fr', url: '/' }, { lang: 'es', url: '/es' }, { lang: 'x-default', url: '/' }]
}) => {
  const baseUrl = 'https://www.invasionlatina.be';
  const fullCanonical = canonical ? `${baseUrl}${canonical}` : baseUrl;
  const defaultOgImage = `${baseUrl}/images/og-image-default.jpg`;
  const finalOgImage = ogImage || defaultOgImage;

  return (
    <Helmet>
      {/* Document language */}
      <html lang={htmlLang} />

      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullCanonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* hreflang alternate language versions */}
      {hreflang && hreflang.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={`${baseUrl}${url}`} />
      ))}

      {/* Open Graph / Facebook / WhatsApp */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:image:width" content={ogImageWidth} />
      <meta property="og:image:height" content={ogImageHeight} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:site_name" content="Invasion Latina" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={finalOgImage} />

      {/* Structured Data (Schema.org) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
